/**
 * Chat Store
 *
 * Zustand store for managing chat state including conversations, messages,
 * active conversation, unread counts, and user presence.
 *
 * @module @municipality/chat/chatStore
 */

import { create } from 'zustand';
import type {
  Conversation,
  Message,
  UserPresence,
  ChatScope,
} from '@municipality/types';
import { chatService } from './chat';

interface ChatStore {
  conversations: Conversation[];
  activeConversationId: string | null;
  messages: Record<string, Message[]>;
  unreadCount: number;
  typingUsers: Record<string, string[]>;
  userPresences: Record<string, UserPresence>;
  isLoading: boolean;
  error: string | null;
  currentScope: ChatScope | null;
  currentMunicipalityId: string | undefined;

  setConversations: (conversations: Conversation[]) => void;
  setActiveConversation: (conversationId: string | null) => void;
  setMessages: (conversationId: string, messages: Message[]) => void;
  prependMessages: (conversationId: string, messages: Message[]) => void;
  addMessage: (conversationId: string, message: Message) => void;
  updateMessage: (conversationId: string, messageId: string, updates: Partial<Message>) => void;
  deleteMessageForUser: (conversationId: string, messageId: string, userId: string) => void;
  setTypingUsers: (conversationId: string, userIds: string[]) => void;
  updatePresence: (userId: string, presence: UserPresence) => void;
  setUnreadCount: (count: number) => void;
  incrementUnreadCount: () => void;
  clearUnreadCount: (conversationId: string, userId: string) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setScope: (scope: ChatScope, municipalityId?: string) => void;

  loadConversations: (userId: string, scope: ChatScope, municipalityId?: string) => Promise<void>;
  loadMessages: (conversationId: string) => Promise<void>;
  sendMessage: (
    conversationId: string,
    content: string,
    senderId: string,
    senderName: string,
    type?: 'text' | 'emoji' | 'system',
    metadata?: { mentions?: string[]; replyTo?: string }
  ) => Promise<void>;
  createDirectConversation: (
    userId: string,
    recipientId: string,
    scope: ChatScope,
    municipalityId?: string
  ) => Promise<Conversation | null>;
  createGroupConversation: (
    name: string,
    participantIds: string[],
    createdBy: string,
    scope: ChatScope,
    municipalityId?: string,
    description?: string
  ) => Promise<Conversation | null>;
  markAsRead: (conversationId: string, messageIds: string[], userId: string) => Promise<void>;
  setTypingStatus: (conversationId: string, userId: string, isTyping: boolean) => Promise<void>;
  archiveConversation: (conversationId: string, userId: string, isArchived: boolean) => Promise<void>;
  muteConversation: (conversationId: string, userId: string, isMuted: boolean) => Promise<void>;
  pinConversation: (conversationId: string, userId: string, isPinned: boolean) => Promise<void>;
  reactToMessage: (conversationId: string, messageId: string, userId: string, emoji: string) => Promise<void>;
  removeReaction: (conversationId: string, messageId: string, userId: string) => Promise<void>;
  addParticipantsToGroup: (conversationId: string, userIds: string[], addedBy: string) => Promise<void>;
  removeParticipantFromGroup: (conversationId: string, userIdToRemove: string, removedBy: string) => Promise<void>;
  renameGroupChat: (conversationId: string, newName: string, userId: string) => Promise<void>;
  deleteConversation: (conversationId: string, userId: string) => Promise<void>;
  editMessage: (conversationId: string, messageId: string, newContent: string, userId: string) => Promise<void>;
}

export const useChatStore = create<ChatStore>((set, get) => ({
  conversations: [],
  activeConversationId: null,
  messages: {},
  unreadCount: 0,
  typingUsers: {},
  userPresences: {},
  isLoading: false,
  error: null,
  currentScope: null,
  currentMunicipalityId: undefined,

  setConversations: (conversations) => {
    const uniqueConversations = Array.from(
      new Map(conversations.map((conv) => [conv.id, conv])).values()
    );
    set({ conversations: uniqueConversations });
  },

  setActiveConversation: (conversationId) => set({ activeConversationId: conversationId }),

  setMessages: (conversationId, messages) =>
    set((state) => {
      const currentMessages = state.messages[conversationId] || [];
      if (currentMessages.length === messages.length) {
        const currentIds = currentMessages.map((m) => m.id).join(',');
        const newIds = messages.map((m) => m.id).join(',');
        if (currentIds === newIds) {
          const hasReactionChanges = currentMessages.some((currentMsg, index) => {
            const newMsg = messages[index];
            if (!newMsg || currentMsg.id !== newMsg.id) return true;
            if (JSON.stringify(currentMsg.reactions || {}) !== JSON.stringify(newMsg.reactions || {}))
              return true;
            if (currentMsg.isEdited !== newMsg.isEdited) return true;
            if (currentMsg.deletedBy?.length !== newMsg.deletedBy?.length) return true;
            return false;
          });
          if (!hasReactionChanges) return state;
        }
      }
      return {
        messages: { ...state.messages, [conversationId]: messages },
      };
    }),

  prependMessages: (conversationId, olderMessages) =>
    set((state) => {
      const currentMessages = state.messages[conversationId] || [];
      const existingIds = new Set(currentMessages.map((m) => m.id));
      const newMessages = olderMessages.filter((m) => !existingIds.has(m.id));
      return {
        messages: {
          ...state.messages,
          [conversationId]: [...newMessages, ...currentMessages],
        },
      };
    }),

  addMessage: (conversationId, message) =>
    set((state) => {
      const existingMessages = state.messages[conversationId] || [];
      if (existingMessages.some((m) => m.id === message.id)) return state;
      return {
        messages: {
          ...state.messages,
          [conversationId]: [...existingMessages, message],
        },
      };
    }),

  updateMessage: (conversationId, messageId, updates) =>
    set((state) => ({
      messages: {
        ...state.messages,
        [conversationId]:
          state.messages[conversationId]?.map((msg) =>
            msg.id === messageId ? { ...msg, ...updates } : msg
          ) ?? [],
      },
    })),

  deleteMessageForUser: (conversationId, messageId, userId) =>
    set((state) => ({
      messages: {
        ...state.messages,
        [conversationId]:
          state.messages[conversationId]?.map((msg) =>
            msg.id === messageId ? { ...msg, deletedBy: [...msg.deletedBy, userId] } : msg
          ) ?? [],
      },
    })),

  setTypingUsers: (conversationId, userIds) =>
    set((state) => ({
      typingUsers: { ...state.typingUsers, [conversationId]: userIds },
    })),

  updatePresence: (userId, presence) =>
    set((state) => ({
      userPresences: { ...state.userPresences, [userId]: presence },
    })),

  setUnreadCount: (count) => set({ unreadCount: count }),
  incrementUnreadCount: () => set((state) => ({ unreadCount: state.unreadCount + 1 })),
  clearUnreadCount: (conversationId, userId) => {
    const state = get();
    const totalUnread = state.conversations
      .filter((conv) => conv.id !== conversationId)
      .reduce((sum, conv) => sum + (conv.unreadCount[userId] ?? 0), 0);
    set({ unreadCount: totalUnread });
  },
  setLoading: (loading) => set({ isLoading: loading }),
  setError: (error) => set({ error }),
  setScope: (scope, municipalityId) => set({ currentScope: scope, currentMunicipalityId: municipalityId }),

  loadConversations: async (userId, scope, municipalityId) => {
    set({ isLoading: true, error: null });
    try {
      const conversations = await chatService.getConversations(userId, scope, municipalityId);
      const totalUnread = conversations.reduce(
        (sum, conv) => sum + (conv.unreadCount[userId] ?? 0),
        0
      );
      set({ conversations, unreadCount: totalUnread, isLoading: false });
    } catch (error) {
      console.error('Failed to load conversations:', error);
      set({ error: 'Failed to load conversations', isLoading: false });
    }
  },

  loadMessages: async (conversationId) => {
    set({ isLoading: true, error: null });
    try {
      const messages = await chatService.getMessages(conversationId, 50);
      set((state) => ({
        messages: { ...state.messages, [conversationId]: messages },
        isLoading: false,
      }));
    } catch (error) {
      console.error('Failed to load messages:', error);
      set({ error: 'Failed to load messages', isLoading: false });
    }
  },

  sendMessage: async (conversationId, content, senderId, senderName, type, metadata) => {
    try {
      const message = await chatService.sendMessage(
        conversationId,
        content,
        senderId,
        senderName,
        type ?? 'text',
        metadata
      );
      get().addMessage(conversationId, message);
    } catch (error) {
      console.error('Failed to send message:', error);
      set({ error: 'Failed to send message' });
      throw error;
    }
  },

  createDirectConversation: async (userId, recipientId, scope, municipalityId) => {
    set({ isLoading: true, error: null });
    try {
      const conversation = await chatService.createDirectConversation(
        userId,
        recipientId,
        scope,
        municipalityId
      );
      set({ isLoading: false });
      return conversation;
    } catch (error) {
      console.error('Failed to create conversation:', error);
      set({ error: 'Failed to create conversation', isLoading: false });
      return null;
    }
  },

  createGroupConversation: async (
    name,
    participantIds,
    createdBy,
    scope,
    municipalityId,
    description
  ) => {
    set({ isLoading: true, error: null });
    try {
      const conversation = await chatService.createGroupConversation(
        name,
        participantIds,
        createdBy,
        scope,
        municipalityId,
        description
      );
      set({ isLoading: false });
      return conversation;
    } catch (error) {
      console.error('Failed to create group conversation:', error);
      set({ error: 'Failed to create group conversation', isLoading: false });
      return null;
    }
  },

  markAsRead: async (conversationId, messageIds, userId) => {
    try {
      await chatService.markAsRead(conversationId, messageIds, userId);
      get().clearUnreadCount(conversationId, userId);
    } catch (error) {
      console.error('Failed to mark as read:', error);
    }
  },

  setTypingStatus: async (conversationId, userId, isTyping) => {
    try {
      await chatService.setTyping(conversationId, userId, isTyping);
    } catch (error) {
      console.error('Failed to set typing status:', error);
    }
  },

  archiveConversation: async (conversationId, userId, isArchived) => {
    try {
      await chatService.archiveConversation(conversationId, userId, isArchived);
      set((state) => ({
        conversations: state.conversations.map((conv) =>
          conv.id === conversationId
            ? { ...conv, isArchived: { ...conv.isArchived, [userId]: isArchived } }
            : conv
        ),
      }));
    } catch (error) {
      console.error('Failed to archive conversation:', error);
      set({ error: 'Failed to archive conversation' });
    }
  },

  muteConversation: async (conversationId, userId, isMuted) => {
    try {
      await chatService.muteConversation(conversationId, userId, isMuted);
      set((state) => ({
        conversations: state.conversations.map((conv) =>
          conv.id === conversationId
            ? { ...conv, isMuted: { ...conv.isMuted, [userId]: isMuted } }
            : conv
        ),
      }));
    } catch (error) {
      console.error('Failed to mute conversation:', error);
      set({ error: 'Failed to mute conversation' });
    }
  },

  pinConversation: async (conversationId, userId, isPinned) => {
    try {
      await chatService.pinConversation(conversationId, userId, isPinned);
      set((state) => {
        const conversation = state.conversations.find((conv) => conv.id === conversationId);
        if (!conversation) return state;
        const pinnedBy = conversation.pinnedBy ?? [];
        const updatedPinnedBy = isPinned
          ? pinnedBy.includes(userId)
            ? pinnedBy
            : [...pinnedBy, userId]
          : pinnedBy.filter((id: string) => id !== userId);
        return {
          conversations: state.conversations.map((conv) =>
            conv.id === conversationId ? { ...conv, pinnedBy: updatedPinnedBy } : conv
          ),
        };
      });
    } catch (error) {
      console.error('Failed to pin conversation:', error);
      set({ error: 'Failed to pin conversation' });
    }
  },

  reactToMessage: async (conversationId, messageId, userId, emoji) => {
    try {
      const state = get();
      const message = (state.messages[conversationId] ?? []).find((m) => m.id === messageId);
      if (message) {
        get().updateMessage(conversationId, messageId, {
          reactions: { ...(message.reactions ?? {}), [userId]: emoji },
        });
      }
      await chatService.reactToMessage(conversationId, messageId, userId, emoji);
    } catch (error) {
      console.error('Failed to add reaction:', error);
      set({ error: 'Failed to add reaction' });
      const state = get();
      const message = (state.messages[conversationId] ?? []).find((m) => m.id === messageId);
      if (message) {
        const reverted = { ...(message.reactions ?? {}) };
        delete reverted[userId];
        get().updateMessage(conversationId, messageId, { reactions: reverted });
      }
    }
  },

  removeReaction: async (conversationId, messageId, userId) => {
    try {
      const state = get();
      const message = (state.messages[conversationId] ?? []).find((m) => m.id === messageId);
      if (message) {
        const updated = { ...(message.reactions ?? {}) };
        delete updated[userId];
        get().updateMessage(conversationId, messageId, { reactions: updated });
      }
      await chatService.removeReaction(conversationId, messageId, userId);
    } catch (error) {
      console.error('Failed to remove reaction:', error);
      set({ error: 'Failed to remove reaction' });
    }
  },

  addParticipantsToGroup: async (conversationId, userIds, addedBy) => {
    try {
      await chatService.addParticipantsToGroup(conversationId, userIds, addedBy);
    } catch (error) {
      console.error('Failed to add participants:', error);
      set({ error: 'Failed to add participants' });
      throw error;
    }
  },

  removeParticipantFromGroup: async (conversationId, userIdToRemove, removedBy) => {
    try {
      await chatService.removeParticipantFromGroup(conversationId, userIdToRemove, removedBy);
      if (userIdToRemove === removedBy && get().activeConversationId === conversationId) {
        set({ activeConversationId: null });
      }
    } catch (error) {
      console.error('Failed to remove participant:', error);
      set({ error: 'Failed to remove participant' });
      throw error;
    }
  },

  renameGroupChat: async (conversationId, newName, userId) => {
    try {
      await chatService.updateConversationName(conversationId, newName, userId);
    } catch (error) {
      console.error('Failed to rename group chat:', error);
      set({ error: 'Failed to rename group chat' });
      throw error;
    }
  },

  deleteConversation: async (conversationId, userId) => {
    try {
      await chatService.deleteConversation(conversationId, userId);
      const state = get();
      if (state.activeConversationId === conversationId) {
        set({ activeConversationId: null });
      }
      const updatedMessages = { ...state.messages };
      delete updatedMessages[conversationId];
      set({ messages: updatedMessages });
    } catch (error) {
      console.error('Failed to delete conversation:', error);
      set({ error: 'Failed to delete conversation' });
      throw error;
    }
  },

  editMessage: async (conversationId, messageId, newContent, userId) => {
    try {
      const state = get();
      const currentMessages = state.messages[conversationId] ?? [];
      const messageIndex = currentMessages.findIndex((msg) => msg.id === messageId);
      if (messageIndex === -1) throw new Error('Message not found in store');
      const originalMessage = currentMessages[messageIndex];
      const optimisticMessage: Message = {
        ...originalMessage,
        content: newContent.trim(),
        isEdited: true,
        editedAt: new Date(),
        updatedAt: new Date(),
      };
      const updatedMessages = [...currentMessages];
      updatedMessages[messageIndex] = optimisticMessage;
      set((s) => ({
        messages: { ...s.messages, [conversationId]: updatedMessages },
      }));
      await chatService.editMessage(conversationId, messageId, newContent, userId);
    } catch (error) {
      await get().loadMessages(conversationId);
      console.error('Failed to edit message:', error);
      set({ error: 'Failed to edit message' });
      throw error;
    }
  },
}));
